import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../../../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputBase,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search as SearchIcon, Launch as LaunchIcon } from '@mui/icons-material';
import { EmptyState, LoadingSpinner, StatusBadge, CopyableText } from '../../../../../components/common';
import { DataTablePagination } from '../../../../../components/ui';
import { MissionService } from '../../../../../services/mission/MissionService';
import { formatDateTime } from '../../../../../utils/dateUtils';
import { getCallSignDisplayName } from '../../../../../utils/generalUtils';
import type { Mission, PaginatedMissions } from '../../../../../types/mission';
import type { UserDetail } from '../../../../../types/user';

interface MissionsTabProps {
  userDetail: UserDetail;
}

export const MissionsTab: React.FC<MissionsTabProps> = ({ userDetail }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchMissions = useCallback(async () => {
    try {
      setIsLoading(true);
      // Use id or userId, whichever is available
      const userId = userDetail.id || userDetail.userId;
      if (!userId) {
        console.error('User ID is missing');
        setMissions([]);
        return;
      }
      const result: PaginatedMissions = await MissionService.getMissions(
        page,
        pageSize,
        {
          userId: userId,
          search: debouncedSearchValue || undefined,
        }
      );
      setMissions(result.data);
      setTotalResults(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to fetch missions:', error);
      setMissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, userDetail.id, userDetail.userId, debouncedSearchValue]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalResults);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(2),
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '1rem',
          }}
        >
          Mission History
        </Typography>
        <Box
          sx={{
            position: 'relative',
            borderRadius: theme.shapes.borderRadius.md,
            backgroundColor: theme.customColors.searchBackground,
            border: `1px solid ${theme.customColors.searchBorder}`,
            width: 250,
          }}
        >
          <Box
            sx={{
              padding: theme.spacing(0, 1),
              height: '100%',
              position: 'absolute',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
          </Box>
          <InputBase
            placeholder="Search missions..."
            value={searchValue}
            onChange={handleSearchChange}
            sx={{
              color: theme.palette.text.primary,
              width: '100%',
              padding: theme.spacing(1, 1, 1, 4),
              fontSize: '0.875rem',
            }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : missions.length === 0 ? (
        <EmptyState
          title="No Missions Found"
          message={searchValue ? "No missions match your search criteria." : `This ${userDetail.userType === 'ghost' ? 'Ghost' : 'Asset'} has no mission history yet.`}
        />
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Mission Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Mission Title
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {userDetail.userType === 'ghost' ? 'Asset' : 'Ghost'}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Created
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {missions.map((mission) => (
                <TableRow key={mission.id}>
                  <TableCell>
                    <CopyableText
                      text={mission.code}
                      onTextClick={() => navigate(`/missions/${mission.id}`)}
                      sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {mission.title}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={mission.status} />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary }}>
                    {userDetail.userType === 'ghost'
                      ? getCallSignDisplayName(mission.assetCallSign, 'Asset')
                      : (mission.ghostId || mission.ghostCallSign || mission.ghostFirstName
                          ? getCallSignDisplayName(mission.ghostCallSign, 'Ghost')
                          : 'Unassigned')
                    }
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary }}>
                    {formatDateTime(mission.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      onClick={() => navigate(`/missions/${mission.id}`)}
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      View
                      <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <DataTablePagination
            totalPages={totalPages}
            page={page}
            onPageChange={handlePageChange}
            showStats
            startIndex={startIndex}
            endIndex={endIndex}
            totalResults={totalResults}
            layout="space-between"
          />
        </>
      )}
    </Box>
  );
};

